import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { BusyLabel } from "../components/loader/index.js";
import { useErrorOnArrival, useToast } from "../components/toast.js";
import { googleSignInHref, signIn } from "../lib/api/index.js";
import { authErrorFrom } from "../lib/auth-errors.js";
import { MIN_PASSWORD_LENGTH } from "../lib/password.js";
import { queryKeys } from "../lib/query-keys.js";
import { documentTitle, usePageTitle } from "../lib/routes.js";
import { useUrlFilters } from "../lib/url-filters.js";
import { AuthShell } from "./auth-shell.js";
import { GoogleButton } from "./google-button.js";
import { LinkForm } from "./sign-in-link-form.js";

/**
 * Sign in.
 *
 * Google first, and the only way to make an account: every address that joins
 * is one Google has verified, which is what lets an invite trust an email
 * (plans/google-auth-and-invites.md, D1). An account from before Google keeps
 * its password, behind one quiet control — nobody who could sign in yesterday
 * is locked out today (AGENTS.md §0f). There is no register form.
 */

type State = { readonly kind: "idle" } | { readonly kind: "working" };

export function SignInScreen() {
  usePageTitle(documentTitle("Sign in"));
  // Where a refused Google sign-in lands: one sentence, read from the address
  // the API redirected to, and said in a toast that stays until closed.
  useErrorOnArrival(
    "Google sign-in did not happen",
    authErrorFrom(useUrlFilters().params),
  );
  // Google first; the two ways in for an account that already exists sit
  // behind quiet controls, one open at a time.
  const [other, setOther] = useState<"none" | "password" | "link" | "reset">("none");

  return (
    <AuthShell>
      <h1 className="auth-title">Sign in or create an account</h1>
      <p className="auth-lede">
        New to Crux? Continue with Google and your account is made in the same step.
      </p>

      <GoogleButton href={googleSignInHref()} />

      <div className="auth-or">or</div>
      {other === "password" ? <PasswordForm /> : null}
      {other === "link" ? <LinkForm /> : null}
      {other === "reset" ? (
        <>
          <p className="auth-lede">
            Enter your email and we will send a link to choose a new password.
          </p>
          <LinkForm purpose="password-reset" />
        </>
      ) : null}
      <div className="auth-others">
        {other === "password" ? (
          <button
            type="button"
            className="button-link auth-password-open"
            onClick={() => {
              setOther("reset");
            }}
          >
            Forgot your password?
          </button>
        ) : null}
        {other === "password" ? null : (
          <button
            type="button"
            className="button-link auth-password-open"
            onClick={() => {
              setOther("password");
            }}
          >
            Sign in with email and password
          </button>
        )}
        {other === "link" ? null : (
          <button
            type="button"
            className="button-link auth-password-open"
            onClick={() => {
              setOther("link");
            }}
          >
            Email me a sign-in link
          </button>
        )}
      </div>
    </AuthShell>
  );
}

/** For accounts made before Google, which still have a password. */
function PasswordForm() {
  const client = useQueryClient();
  const [email, setEmail] = useState("");
  const toast = useToast();
  const [password, setPassword] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [state, setState] = useState<State>({ kind: "idle" });

  const submit = (event: { preventDefault: () => void }): void => {
    event.preventDefault();
    setState({ kind: "working" });
    signIn(email, password)
      // The session cookie is set; re-ask who we are rather than assuming.
      .then(async () => {
        await client.invalidateQueries({ queryKey: queryKeys.me() });
      })
      .catch((error: unknown) => {
        setState({ kind: "idle" });
        toast.failed("Could not sign in", error);
      });
  };

  return (
    <form onSubmit={submit}>
      <div className="auth-fields">
        <div className="auth-field">
          <label className="auth-label" htmlFor="email">
            Email address
          </label>
          <input
            id="email"
            className="text-input"
            type="email"
            required
            autoFocus
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </div>

        <div className="auth-field">
          {/*
            The reveal shares the label's row: pinned inside the input it
            collided with the password manager's own badge, which owns that
            corner of every password field on the web.
          */}
          <div className="auth-label-row">
            <label className="auth-label" htmlFor="password">
              Password
            </label>
            <button
              className="auth-reveal"
              type="button"
              aria-pressed={revealed}
              aria-controls="password"
              onClick={() => {
                setRevealed(!revealed);
              }}
            >
              {revealed ? "Hide" : "Show"}
            </button>
          </div>
          <input
            id="password"
            className="text-input"
            type={revealed ? "text" : "password"}
            required
            minLength={MIN_PASSWORD_LENGTH}
            autoComplete="current-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            onKeyUp={(event) => {
              setCapsLock(event.getModifierState("CapsLock"));
            }}
          />
          {/* A typed password that "does not match" is most often this. */}
          {capsLock ? <p className="field-hint">Caps Lock is on.</p> : null}
        </div>
      </div>

      <button
        className="button-primary auth-submit"
        type="submit"
        disabled={state.kind === "working"}
      >
        <BusyLabel busy={state.kind === "working"} idle="Sign in" working="Working…" />
      </button>
    </form>
  );
}
